import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatInTimeZone } from 'date-fns-tz';
import { useAtom } from 'jotai';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';

import { selectedNoteIdAtom } from '../state';

const BLURB_DELIMITER = '\n\n==========\n\n';

/**
 * Formats the patient consent blurb for display.
 */
const formatConsentBlurb = (consent: {
  id: string;
  fullName?: string | null;
  dob?: string | null;
  createdAtISO?: Date | string | null;
}): string => {
  try {
    const createdAt = consent.createdAtISO
      ? consent.createdAtISO instanceof Date
        ? consent.createdAtISO
        : new Date(consent.createdAtISO)
      : null;
    const isValidDate =
      createdAt instanceof Date && !isNaN(createdAt.getTime());

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const consentGrantedAt = isValidDate
      ? formatInTimeZone(
          createdAt,
          userTimeZone,
          'MMMM d, yyyy @ hh:mm:ss a zzz',
        )
      : 'An unknown date';

    return `On ${consentGrantedAt}, ${consent.fullName} ${
      consent.dob ? `(DOB ${consent.dob})` : ''
    } gave verbal consent for audio recording for documentation during this encounter. Purpose explained and opt-out offered; consent granted.\nConsent Ref: [${
      consent.id
    }]`;
  } catch (e) {
    console.error('Error formatting consent date:', e);
    return `Verbal consent for recording was granted, but the date could not be parsed. Consent Ref: [${consent.id}].`;
  }
};

export const getNoteSummaryWithoutBlurb = (
  noteSummary: string,
  hasWritebackEnabled: boolean,
): string => {
  return hasWritebackEnabled
    ? noteSummary
    : noteSummary.split(BLURB_DELIMITER)[0];
};

interface UseNoteManagementProps {
  clinicalNotesData: ClinicalNote[];
  hasWritebackEnabled: boolean;
  enableVoiceNotesConsent: boolean;
}

interface UseNoteManagementReturn {
  selectedNote: ClinicalNote | undefined;
  noteContent: string;
  patientConsentBlurb: string | undefined;
  isSavingNoteUpdate: boolean;
  // Derived note status
  isNoteSavedToPMS: boolean;
  isNoteBeingSavedToPMS: boolean;
  isNoteReadOnly: boolean;
  // Handlers
  setNoteContent: (content: string) => void;
  handleSetSelectedNoteId: (noteId: string | undefined) => void;
  saveNoteBeforeClose: () => void;
}

export const useNoteManagement = ({
  clinicalNotesData,
  hasWritebackEnabled,
  enableVoiceNotesConsent,
}: UseNoteManagementProps): UseNoteManagementReturn => {
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNoteUpdate, setIsSavingNoteUpdate] = useState(false);
  const [patientConsentBlurb, setPatientConsentBlurb] = useState<
    string | undefined
  >(undefined);

  // Use atom for selected note ID - persists across sheet open/close like patient name
  const [selectedNoteId, setSelectedNoteId] = useAtom(selectedNoteIdAtom);

  const { mutate: updateNote } = useUpdateNotes();
  const prevContentRef = useRef(noteContent);

  // Derive selectedNote from clinicalNotesData + selectedNoteId
  const selectedNote = useMemo(() => {
    if (!selectedNoteId || !Array.isArray(clinicalNotesData)) return undefined;
    return clinicalNotesData.find((note) => note.id === selectedNoteId);
  }, [selectedNoteId, clinicalNotesData]);

  const handleSetSelectedNoteId = useCallback(
    (noteId: string | undefined) => {
      setSelectedNoteId(noteId);
    },
    [setSelectedNoteId],
  );

  // Force save any pending changes (used before closing)
  const saveNoteBeforeClose = useCallback(() => {
    const noteWithoutBlurb = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );
    if (selectedNote?.id && prevContentRef.current !== noteWithoutBlurb) {
      updateNote({
        clinicalNoteId: selectedNote.id,
        updateNotesBody: {
          editedSummary: noteWithoutBlurb,
        },
      });
    }
  }, [selectedNote?.id, noteContent, hasWritebackEnabled, updateNote]);

  // Auto-select newest note when none is selected and notes are available
  useEffect(() => {
    if (!Array.isArray(clinicalNotesData) || clinicalNotesData.length === 0) {
      return;
    }

    // Only auto-select if no valid note is currently selected
    if (!selectedNote) {
      const newestNote = clinicalNotesData.reduce((latest, current) =>
        new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest,
      );
      setSelectedNoteId(newestNote.id);
    }
  }, [clinicalNotesData, selectedNote, setSelectedNoteId]);

  // Sync noteContent and consent blurb when selectedNote changes
  // (consolidated from two separate useEffects)
  useEffect(() => {
    // Clear state when no note is selected (e.g., switched to patient with no notes)
    // This prevents stale data from previous patient being displayed
    if (!selectedNote) {
      setNoteContent('');
      setPatientConsentBlurb(undefined);
      prevContentRef.current = '';
      return;
    }

    const summaryContent =
      selectedNote.editedSummary || selectedNote.summary || '';
    const consent = selectedNote.patientConsent;

    // Handle consent blurb formatting
    if (consent && enableVoiceNotesConsent) {
      const blurb = formatConsentBlurb(consent);

      if (hasWritebackEnabled) {
        setPatientConsentBlurb(blurb);
        setNoteContent(summaryContent);
      } else {
        // Append blurb to content when writeback is disabled
        setPatientConsentBlurb(undefined);
        setNoteContent(`${summaryContent}${BLURB_DELIMITER}${blurb}`);
      }
    } else {
      setPatientConsentBlurb(undefined);
      setNoteContent(summaryContent);
    }

    prevContentRef.current = summaryContent;
  }, [selectedNote, hasWritebackEnabled, enableVoiceNotesConsent]);

  // Simple debounced auto-save for note content changes
  useEffect(() => {
    if (!selectedNote?.id) return;

    // Only save if content actually changed
    const noteWithoutBlurb = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );
    if (prevContentRef.current === noteWithoutBlurb) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsSavingNoteUpdate(true);
      updateNote(
        {
          clinicalNoteId: selectedNote.id,
          updateNotesBody: {
            editedSummary: noteWithoutBlurb,
          },
        },
        {
          onSuccess: () => {
            setIsSavingNoteUpdate(false);
            prevContentRef.current = noteWithoutBlurb;
          },
          onError: () => {
            setIsSavingNoteUpdate(false);
          },
        },
      );
    }, 1000); // 1 second delay

    return () => clearTimeout(timeoutId);
  }, [noteContent, selectedNote?.id, hasWritebackEnabled, updateNote]);

  // Derived state for note PMS status
  const isNoteSavedToPMS = useMemo(() => {
    return Boolean(
      selectedNote?.writebackStatus?.isProcessed &&
        selectedNote?.writebackStatus?.processedAt &&
        selectedNote?.writebackStatus?.errorCode === null,
    );
  }, [selectedNote?.writebackStatus]);

  const isNoteBeingSavedToPMS = useMemo(() => {
    return Boolean(
      selectedNote?.writebackStatus?.clinicalNoteId &&
        !selectedNote?.writebackStatus?.processedAt &&
        selectedNote?.writebackStatus?.errorCode === null,
    );
  }, [selectedNote?.writebackStatus]);

  const isNoteReadOnly = isNoteSavedToPMS || isNoteBeingSavedToPMS;

  return {
    selectedNote,
    noteContent,
    patientConsentBlurb,
    isSavingNoteUpdate,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isNoteReadOnly,
    setNoteContent,
    handleSetSelectedNoteId,
    saveNoteBeforeClose,
  };
};
